import type { OurFileRouter } from '../server/file-router';
import { generateReactHelpers } from '@uploadthing/react';

type CreateUploadthingClientOptions = {
  apiUrl: string;
  getAuthToken: () => Promise<string | null>;
  /**
   * Extra headers resolved per request. Used by public, token-authorised
   * surfaces (the Final Client Details Form share link) which have no Clerk
   * session and carry their own credential in a header instead.
   */
  getExtraHeaders?: () => Record<string, string> | null;
};

export type UploadthingReactClient = ReturnType<
  typeof generateReactHelpers<OurFileRouter>
>;

// Returns the typed React helpers (`useUploadThing`, `uploadFiles`)
// bound to the consumer-supplied API URL + auth-token resolver. The
// package can't import from `apps/web` directly, so each web app
// initialises its own copy with the right credentials.
export const createUploadthingClient = ({
  apiUrl,
  getAuthToken,
  getExtraHeaders,
}: CreateUploadthingClientOptions): UploadthingReactClient => {
  const authedFetch: typeof fetch = async (input, init) => {
    const token = await getAuthToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const extra = getExtraHeaders?.();
    if (extra) {
      for (const [key, value] of Object.entries(extra)) {
        if (value) headers.set(key, value);
      }
    }
    return fetch(input, { ...init, headers });
  };

  return generateReactHelpers<OurFileRouter>({
    url: apiUrl,
    fetch: authedFetch,
  });
};
